import React from "react";
import { motion } from "framer-motion";
import { Scale, Calculator, Code2, Palette, Building, Briefcase } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { PROFESSIONAL_IMAGE } from "@/lib/constants";

const ROLES = [
  { icon: Scale, label: "Legal Advisors" },
  { icon: Calculator, label: "Chartered Accountants" },
  { icon: Code2, label: "IT Consultants" },
  { icon: Palette, label: "Designers" },
  { icon: Building, label: "Architects" },
  { icon: Briefcase, label: "Freelancers" },
];

export const Professional = () => {
  return (
    <section
      id="professional"
      data-testid={LANDING.professionalSection}
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left: Content */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F25C2A]">
              · Professional Services Payments
            </p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#0A0A0A]">
              A marketplace for
              <br />
              <em className="italic text-[#F25C2A] font-normal">professional services</em>.
            </h2>
            <p className="mt-5 max-w-xl text-lg text-[#4A4A4A] leading-relaxed">
              Pay your CA, lawyer, IT consultant, designer or architect through
              EMDpay. Professionals onboard as KYC-verified sellers — funds
              settle directly to their bank once verification is complete.
            </p>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ROLES.map((r, i) => (
                <motion.div
                  key={r.label}
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  data-testid={`${LANDING.professionalCard}-${i}`}
                  className="group bg-white border border-[#E5E5E0] rounded-2xl p-5 hover:border-[#0A0A0A] transition-all duration-300"
                >
                  <r.icon
                    size={20}
                    className="text-[#F25C2A] group-hover:scale-110 transition-transform"
                  />
                  <p className="mt-4 text-sm font-semibold text-[#0A0A0A] leading-snug">
                    {r.label}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 inline-flex items-center gap-3 px-5 py-4 rounded-2xl bg-[#0A0A0A] text-white">
              <span className="h-2 w-2 rounded-full bg-[#F25C2A]" />
              <p className="text-sm">
                Pay by Credit Card, Prepaid Card, Wallet, EMI or UPI —&nbsp;
                <span className="font-semibold">your choice</span>
              </p>
            </div>
          </div>

          {/* Right: Image card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 order-1 lg:order-2 relative"
          >
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5] bg-[#0A0A0A]">
              <img
                src={PROFESSIONAL_IMAGE}
                alt="Business consultant meeting"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-95"
              />
              <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3.5 py-1.5 text-xs font-semibold text-[#0A0A0A]">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Verified professional · KYC complete
              </div>
              <div className="absolute bottom-5 left-5 right-5 grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#737373]">
                    Pay any pro
                  </p>
                  <p className="mt-1 font-serif text-lg text-[#0A0A0A] leading-tight">
                    Card · UPI · EMI
                  </p>
                </div>
                <div className="bg-[#F25C2A] rounded-2xl p-4 text-white">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">
                    Settles to
                  </p>
                  <p className="mt-1 font-serif text-lg leading-tight">Seller&apos;s bank</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
